import { Alert, Box, Center, Group, Loader, Paper, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useNavigate } from 'react-router';
import { SectionAddAction } from 'src/cashflows/components/SectionAddButton';
import { formatMoney } from 'src/common/currency';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { getCategoryEntries } from 'src/financial/financial-data/rework/addProductEntries';
import { FinancialSectionTable } from 'src/financial/financial-data/rework/FinancialSectionTable';
import { FinancialRow, netWorthOf } from 'src/financial/financial-data/rework/financialRows';
import { useCreateFinancialItem } from 'src/financial/financial-data/financial-item/useCreateFinancialItem';
import { type OwnerSelection } from 'src/financial/financial-data/financial-item/FamilyOwnerModal';
import { RETURN_TO_PARAM } from 'src/financial/financial-data/financial-item/rework/returnPath';
import { FamilyMember } from './useFamilyMembers';
import { useFamilyFinancialItems } from './useFamilyFinancialItems';

export function FamilyFinancialPanel({ members }: { members: FamilyMember[] }) {
  const { categories, isLoading, hasErrors } = useFamilyFinancialItems(members);
  const { flagEnabledFinancialItemOwners } = useFlags();
  const navigate = useNavigate();
  const currency = useCurrency();

  // An item opens against the client it hangs off, not whoever is browsing the family — but it
  // was opened from the family, so that is where saving it returns to.
  const familyId = members.find(member => member.client.familyId)?.client.familyId;
  const returnTo = familyId ? `/family/${familyId}/financial` : undefined;

  const handleRowClick = (row: FinancialRow) => {
    if (!row.clientId) return;
    const query = returnTo ? `?${RETURN_TO_PARAM}=${encodeURIComponent(returnTo)}` : '';
    navigate(`/client/${row.clientId}/financial-items/${row.navProduct}/${row.id}${query}`);
  };

  // An item added from the family view usually belongs to the whole family, so the owner picker
  // opens with every member ticked and the adviser confirms or trims it. The record still has to
  // hang off one client, and that is the main member — the client the family view is anchored on.
  const mainMember = members.find(member => member.isMain) ?? members[0];
  const familyOwners: OwnerSelection[] = members.map(member => ({ client_id: member.clientId }));

  const { beginCreate, modals } = useCreateFinancialItem({
    clientId: mainMember?.clientId ?? '',
    members,
    returnTo,
    // The main member is only where this view is anchored, so an item that cannot record owners
    // asks which member it is for rather than filing itself against them.
    chooseHolder: true,
  });

  const addActionForCategory = (key: string): SectionAddAction | undefined => {
    // Without the owners feature there is nobody to attribute a family-level item to, so the
    // section offers no Add control rather than a button that cannot do anything.
    if (!flagEnabledFinancialItemOwners || mainMember == null) {
      return undefined;
    }
    switch (key) {
      case 'spendingAccounts':
        return { onAdd: () => beginCreate('SPENDING_ACCOUNT', 'SpendingAccount', familyOwners) };
      case 'protections':
        return { onAdd: () => beginCreate('PROTECTION', 'Protection', familyOwners) };
      case 'assets':
        return {
          entries: getCategoryEntries('ASSET'),
          onSelect: product => beginCreate('ASSET', product, familyOwners),
        };
      case 'liabilities':
        return {
          entries: getCategoryEntries('LIABILITY'),
          onSelect: product => beginCreate('LIABILITY', product, familyOwners),
        };
      default:
        return undefined;
    }
  };

  const renderSection = (category: (typeof categories)[number]) => (
    <FinancialSectionTable
      key={category.key}
      title={category.title}
      rows={category.rows}
      total={category.total}
      fullTotal={category.fullTotal}
      totalLabel={category.totalLabel}
      subSections={category.subSections}
      onRowClick={handleRowClick}
      showOwnership
      ownershipLabel="Family owners"
      addAction={addActionForCategory(category.key)}
      hideTotal={category.key === 'protections'}
    />
  );

  // Laid out as a client's own Financials is: what the household holds and owes, then what it
  // comes to, then protection below the line it is left out of.
  const balanceSheetCategories = categories.filter(category => category.key !== 'protections');
  const protectionCategory = categories.find(category => category.key === 'protections');

  return (
    <Box>
      <Stack gap="lg">
        {hasErrors && (
          <Alert
            variant="light"
            color="yellow"
            icon={<IconAlertTriangle size={18} />}
            title="Some data could not be loaded"
          >
            Financial data for one or more family members failed to load, so the totals below may be
            incomplete.
          </Alert>
        )}
        {isLoading ? (
          <Paper variant="content">
            <Center py="xxl">
              <Loader />
            </Center>
          </Paper>
        ) : (
          balanceSheetCategories.map(renderSection)
        )}
      </Stack>

      {/* A figure stood against part-loaded data would read as the household's worth, so it waits
          until every member's items are in. The warning above already covers a member that failed. */}
      {!isLoading && (
        <Group justify="flex-end" gap={8} mt={24}>
          <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.5">
            Net worth:
          </Text>
          <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.10">
            {formatMoney(netWorthOf(categories), currency)}
          </Text>
        </Group>
      )}

      {!isLoading && protectionCategory && <Box mt={24}>{renderSection(protectionCategory)}</Box>}

      {modals}
    </Box>
  );
}
